import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  Code2,
  Database,
  KeyRound,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { INTEGRATIONS } from "@/data/integrations";
import { INTEGRATION_COLORS } from "@/data/integration-colors";
import { cn } from "@/lib/utils";

/**
 * Pull a curated subset out of the registry by slug. Throws at module
 * load if a slug doesn't exist — that's intentional; the showcase
 * should never silently render a letter-tile fallback.
 */
function pickRegistry(slugs: readonly string[]): { slug: string; name: string }[] {
  return slugs.map((slug) => {
    const row = INTEGRATIONS.find((i) => i.slug === slug);
    if (!row) {
      throw new Error(`flow-showcase: slug "${slug}" not in REGISTRY`);
    }
    return { slug: row.slug, name: row.name };
  });
}

/**
 * FlowShowcase v4 — a fixed-dimension, interactive "control plane"
 * dashboard mockup. Renders the same on phones and desktops; the page
 * never reflows around this component because its outer size is locked.
 *
 * Layout:
 *  - Browser chrome top bar (traffic lights + fake URL + status pill).
 *  - Three columns: Providers · MemKernel core · Agents.
 *  - A live activity feed scrolls along the bottom showing fake tool
 *    calls; clicking a provider or agent filters the feed.
 */

// All five providers + five agents below come from the runtime REGISTRY
// — never hardcoded. The `short` label is derived from the slug so it
// stays in sync if the integration is renamed upstream.
const PROVIDERS = pickRegistry([
  "gmail",
  "googledrive",
  "slack",
  "jira",
  "github",
] as const).map((p) => ({ ...p, short: p.slug.replace(/^google/, "") }));

const AGENTS = pickRegistry([
  "anthropic_administrator",
  "openai",
  "gemini",
  "perplexityai",
  "replicate",
] as const);

const MODULES = [
  { id: "tools",   label: "Tools & MCP", icon: Workflow },
  { id: "memory",  label: "Memory",      icon: Database },
  { id: "vectors", label: "Vectors",     icon: Sparkles },
  { id: "skills",  label: "Skills",      icon: Code2    },
  { id: "auth",    label: "Auth & RBAC", icon: KeyRound },
] as const;

// Now that PROVIDERS / AGENTS are derived from the registry at runtime,
// the slug types are just `string`. The literal narrowing the earlier
// hardcoded version offered isn't worth tying us to specific slugs.
type ProviderSlug = string;
type AgentSlug = string;
type ModuleId = (typeof MODULES)[number]["id"];

const PROVIDER_COLOR = (slug: string) =>
  ({
    github:  "#A78BFA",
    notion:  "#A78BFA",
  } as Record<string, string>)[slug] ?? INTEGRATION_COLORS[slug] ?? "#818CF8";

// A small bank of plausible tool calls the activity feed cycles through.
interface CallTemplate {
  /** Slug used both for filtering and for the row's logo in the feed.
   *  May be a slug that isn't one of the 5 left-column providers; in
   *  that case the feed just renders the logo without a column. */
  provider: string;
  agent:    AgentSlug;
  tool:     string;
  module:   ModuleId;
  ms:       number;
}
const CALLS: CallTemplate[] = [
  // Tools & MCP — the bulk of activity
  { provider: "gmail",          agent: "claude",        tool: "gmail.search",            module: "tools",   ms: 42  },
  { provider: "gmail",          agent: "openai",        tool: "gmail.list_threads",      module: "tools",   ms: 63  },
  { provider: "gmail",          agent: "claude",        tool: "gmail.send_reply",        module: "tools",   ms: 128 },
  { provider: "gmail",          agent: "perplexity",    tool: "gmail.label_thread",      module: "tools",   ms: 38  },
  { provider: "googledocs",     agent: "claude",        tool: "docs.create_doc",         module: "tools",   ms: 312 },
  { provider: "googledocs",     agent: "openai",        tool: "docs.find_doc",           module: "tools",   ms: 74  },
  { provider: "googlesheets",   agent: "claude",        tool: "sheets.append_row",       module: "tools",   ms: 96  },
  { provider: "googlesheets",   agent: "perplexity",    tool: "sheets.read_range",       module: "tools",   ms: 51  },
  { provider: "googlecalendar", agent: "claude",        tool: "calendar.create_event",   module: "tools",   ms: 117 },
  { provider: "googlecalendar", agent: "openai",        tool: "calendar.list_events",    module: "tools",   ms: 44  },
  { provider: "slack",          agent: "perplexity",    tool: "slack.post_message",      module: "tools",   ms: 92  },
  { provider: "slack",          agent: "claude",        tool: "slack.search",            module: "tools",   ms: 68  },
  { provider: "slack",          agent: "openai",        tool: "slack.upload_file",       module: "tools",   ms: 245 },
  { provider: "jira",           agent: "githubcopilot", tool: "jira.search_issues",      module: "tools",   ms: 138 },
  { provider: "jira",           agent: "claude",        tool: "jira.add_comment",        module: "tools",   ms: 81  },
  { provider: "jira",           agent: "openai",        tool: "jira.transition_issue",   module: "tools",   ms: 102 },
  { provider: "github",         agent: "githubcopilot", tool: "github.create_issue",     module: "tools",   ms: 221 },
  { provider: "github",         agent: "claude",        tool: "github.read_file",        module: "tools",   ms: 57  },
  { provider: "github",         agent: "githubcopilot", tool: "github.review_pr",        module: "tools",   ms: 304 },
  { provider: "github",         agent: "openai",        tool: "github.merge_pr",         module: "tools",   ms: 412 },
  { provider: "notion",         agent: "perplexity",    tool: "notion.append_page",      module: "tools",   ms: 174 },
  { provider: "notion",         agent: "claude",        tool: "notion.search_pages",     module: "tools",   ms: 89  },
  { provider: "stripe",         agent: "openai",        tool: "stripe.find_customer",    module: "tools",   ms: 71  },
  { provider: "linear",         agent: "githubcopilot", tool: "linear.create_issue",     module: "tools",   ms: 144 },
  // Memory
  { provider: "slack",          agent: "openai",        tool: "memory.recall",           module: "memory",  ms: 18  },
  { provider: "gmail",          agent: "claude",        tool: "memory.write",            module: "memory",  ms: 12  },
  { provider: "notion",         agent: "perplexity",    tool: "memory.recall",           module: "memory",  ms: 21  },
  // Vectors
  { provider: "jira",           agent: "perplexity",    tool: "vectors.search",          module: "vectors", ms: 47  },
  { provider: "googledocs",     agent: "claude",        tool: "vectors.rag",             module: "vectors", ms: 256 },
  { provider: "notion",         agent: "openai",        tool: "vectors.upsert",          module: "vectors", ms: 89  },
  // Skills
  { provider: "github",         agent: "claude",        tool: "skills.run:refactor",     module: "skills",  ms: 184 },
  { provider: "googledocs",     agent: "perplexity",    tool: "skills.run:summarize",    module: "skills",  ms: 312 },
  // Auth (rare — token refresh isn't a chatty operation)
  { provider: "googledocs",     agent: "ollama",        tool: "auth.refresh:google",     module: "auth",    ms: 11  },
];

interface ActivityRow extends CallTemplate {
  id:   number;
  time: string;
}

export function FlowShowcase() {
  const reduce = useReducedMotion();
  const [filter, setFilter] = useState<
    | { kind: "provider"; slug: ProviderSlug }
    | { kind: "agent";    slug: AgentSlug }
    | { kind: "module";   id:   ModuleId }
    | null
  >(null);

  // Live activity feed: prepend a new call every ~900ms, cap at 25.
  const [feed, setFeed] = useState<ActivityRow[]>(() =>
    seedFeed(8),
  );
  const nextIdRef = useRef(1000);
  const visibleRef = useRef(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = !!entry?.isIntersecting;
      },
      { threshold: 0.1 },
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      if (!visibleRef.current) return;
      const t = CALLS[Math.floor(Math.random() * CALLS.length)]!;
      const now = new Date();
      const row: ActivityRow = {
        ...t,
        id: nextIdRef.current++,
        time: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
      };
      setFeed((prev) => [row, ...prev].slice(0, 25));
    }, 900);
    return () => window.clearInterval(id);
  }, [reduce]);

  // Live counter — incremented in lockstep with the feed, plus jitter.
  const [counter, setCounter] = useState(4120);
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      if (!visibleRef.current) return;
      setCounter((c) => c + Math.floor(2 + Math.random() * 7));
    }, 140);
    return () => window.clearInterval(id);
  }, [reduce]);

  // Filter the feed for the activity rail.
  const filteredFeed = useMemo(() => {
    if (!filter) return feed;
    return feed.filter((r) => {
      if (filter.kind === "provider") return r.provider === filter.slug;
      if (filter.kind === "agent")    return r.agent    === filter.slug;
      if (filter.kind === "module")   return r.module   === filter.id;
      return true;
    });
  }, [feed, filter]);

  // Helpers to decide whether a tile is currently "lit" (= part of the
  // active filter or recently called).
  const lastCall = feed[0];
  function providerIsLit(slug: ProviderSlug) {
    if (filter?.kind === "provider") return filter.slug === slug;
    if (filter?.kind === "agent")    return lastCall?.agent === filter.slug && lastCall.provider === slug;
    if (filter?.kind === "module")   return lastCall?.module === filter.id   && lastCall.provider === slug;
    return lastCall?.provider === slug;
  }
  function agentIsLit(slug: AgentSlug) {
    if (filter?.kind === "agent")    return filter.slug === slug;
    if (filter?.kind === "provider") return lastCall?.provider === filter.slug && lastCall.agent === slug;
    if (filter?.kind === "module")   return lastCall?.module === filter.id     && lastCall.agent === slug;
    return lastCall?.agent === slug;
  }
  function moduleIsLit(id: ModuleId) {
    if (filter?.kind === "module") return filter.id === id;
    return lastCall?.module === id;
  }

  function toggleFilter(
    next:
      | { kind: "provider"; slug: ProviderSlug }
      | { kind: "agent";    slug: AgentSlug }
      | { kind: "module";   id:   ModuleId }
  ) {
    setFilter((cur) => {
      if (cur && JSON.stringify(cur) === JSON.stringify(next)) return null;
      return next;
    });
  }

  return (
    <div
      ref={sectionRef}
      className="mx-auto flex h-[620px] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/40 shadow-2xl backdrop-blur sm:h-[640px]"
    >
      {/* ── Browser chrome ──────────────────────────────────── */}
      <div className="flex items-center gap-3 border-b border-border/40 bg-background/40 px-3 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
        </div>
        <div className="flex flex-1 items-center justify-center gap-1.5 truncate rounded-md bg-background/80 px-3 py-1 font-mono text-[11px] text-muted-foreground">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
          app.memkernel.com/projects/market-intel
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 tabular-nums">
          <span className="relative flex h-1.5 w-1.5">
            {!reduce && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
            )}
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="hidden sm:inline">{counter.toLocaleString()}</span>
          <span className="hidden sm:inline">calls/min</span>
          <span className="sm:hidden">live</span>
        </span>
      </div>

      {/* ── Three-column flow ─────────────────────────────── */}
      <div className="grid flex-1 grid-cols-[1fr_auto_1fr] gap-2 overflow-hidden p-3 sm:p-4">
        {/* Providers */}
        <div>
          <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-[10px]">
            Providers
          </p>
          <div className="space-y-1.5">
            {PROVIDERS.map((p) => {
              const lit = providerIsLit(p.slug);
              const color = PROVIDER_COLOR(p.slug);
              return (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => toggleFilter({ kind: "provider", slug: p.slug })}
                  className={cn(
                    "group relative flex w-full flex-col items-center gap-1 rounded-lg border bg-card/50 p-1.5 transition-colors",
                    lit
                      ? "border-foreground/30 bg-card"
                      : "border-border/60 hover:border-foreground/20",
                  )}
                  style={
                    lit
                      ? { boxShadow: `0 0 0 1px ${color}55, 0 0 20px -8px ${color}` }
                      : undefined
                  }
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
                    <IntegrationLogo slug={p.slug} name={p.name} size={20} />
                  </span>
                  <span className="w-full truncate text-center text-[10px] font-medium leading-tight">
                    {p.name}
                  </span>
                  {lit && !reduce && (
                    <span
                      className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full"
                      style={{
                        background: color,
                        boxShadow: `0 0 6px ${color}`,
                      }}
                    />
                  )}
                </button>
              );
            })}
            <div className="rounded-lg border border-dashed border-border/40 px-2 py-1 text-center text-[9px] text-muted-foreground sm:text-[10px]">
              + 314 more
            </div>
          </div>
        </div>

        {/* Core */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-[140px] sm:w-[180px]">
            <div className="absolute inset-0 -m-3 rounded-2xl bg-gradient-to-br from-indigo-500/30 via-fuchsia-500/30 to-teal-400/30 blur-xl" />
            <div className="relative rounded-2xl border border-foreground/10 bg-gradient-to-br from-indigo-500/10 via-fuchsia-500/10 to-teal-400/10 p-2 backdrop-blur">
              <div className="mb-2 flex items-center gap-1.5 px-1">
                <div className="flex h-5 w-5 items-center justify-center rounded-md bg-foreground text-[9px] font-extrabold text-background">
                  mk
                </div>
                <p className="text-[10px] font-semibold leading-none">MemKernel</p>
              </div>
              <ul className="space-y-1">
                {MODULES.map((m) => {
                  const lit = moduleIsLit(m.id);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => toggleFilter({ kind: "module", id: m.id })}
                        className={cn(
                          "flex w-full items-center gap-1.5 rounded-md border bg-background/50 px-1.5 py-1 text-left transition-colors",
                          lit
                            ? "border-foreground/30 bg-background"
                            : "border-foreground/10 hover:border-foreground/20",
                        )}
                      >
                        <m.icon className={cn("h-2.5 w-2.5 shrink-0", lit ? "text-foreground" : "text-foreground/60")} />
                        <span className="truncate text-[9px] font-medium leading-none">
                          {m.label}
                        </span>
                        {lit && (
                          <span className="ml-auto h-1 w-1 shrink-0 rounded-full bg-emerald-400" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Agents */}
        <div>
          <p className="mb-1.5 text-right text-[9px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-[10px]">
            Agents
          </p>
          <div className="space-y-1.5">
            {AGENTS.map((a) => {
              const lit = agentIsLit(a.slug);
              const color = PROVIDER_COLOR(a.slug);
              return (
                <button
                  key={a.slug}
                  type="button"
                  onClick={() => toggleFilter({ kind: "agent", slug: a.slug })}
                  className={cn(
                    "group relative flex w-full flex-col items-center gap-1 rounded-lg border bg-card/50 p-1.5 transition-colors",
                    lit
                      ? "border-foreground/30 bg-card"
                      : "border-border/60 hover:border-foreground/20",
                  )}
                  style={
                    lit
                      ? { boxShadow: `0 0 0 1px ${color}55, 0 0 20px -8px ${color}` }
                      : undefined
                  }
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
                    <IntegrationLogo slug={a.slug} name={a.name} size={20} />
                  </span>
                  <span className="w-full truncate text-center text-[10px] font-medium leading-tight">
                    {a.name}
                  </span>
                  {lit && !reduce && (
                    <span
                      className="absolute left-1 top-1 h-1.5 w-1.5 rounded-full"
                      style={{
                        background: color,
                        boxShadow: `0 0 6px ${color}`,
                      }}
                    />
                  )}
                </button>
              );
            })}
            <div className="rounded-lg border border-dashed border-border/40 px-2 py-1 text-center text-[9px] text-muted-foreground sm:text-[10px]">
              + any agent
            </div>
          </div>
        </div>
      </div>

      {/* ── Live activity feed ───────────────────────────── */}
      <div className="border-t border-border/40 bg-background/30 px-3 py-2 sm:px-4">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Activity className="h-3 w-3" />
            Live activity
            {filter && (
              <button
                type="button"
                onClick={() => setFilter(null)}
                className="ml-1 rounded-full border border-foreground/15 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-muted-foreground hover:text-foreground"
              >
                clear filter ✕
              </button>
            )}
          </div>
          <span className="flex items-center gap-1 text-[10px] tabular-nums text-muted-foreground">
            <Zap className="h-2.5 w-2.5" />
            {filteredFeed.length} shown
          </span>
        </div>
        <div className="h-[120px] overflow-hidden font-mono text-[10px] sm:text-[11px]">
          <div className="space-y-0.5">
            {filteredFeed.slice(0, 8).map((r) => (
              <motion.div
                key={r.id}
                layout
                initial={reduce ? false : { opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-[auto_18px_auto_auto_auto_auto] items-center gap-1.5 sm:gap-2"
              >
                <span className="text-muted-foreground/70">{r.time}</span>
                <span className="flex h-4 w-4 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
                  <IntegrationLogo slug={r.provider} name={r.provider} size={10} />
                </span>
                <span className="truncate">{r.tool}</span>
                <span className="text-muted-foreground">→</span>
                <span className="truncate text-foreground/70">{r.agent}</span>
                <span className="text-right tabular-nums text-muted-foreground">
                  {r.ms} ms
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function seedFeed(n: number): ActivityRow[] {
  const out: ActivityRow[] = [];
  const now = new Date();
  let id = 0;
  for (let i = 0; i < n; i++) {
    const t = CALLS[i % CALLS.length]!;
    const stamp = new Date(now.getTime() - i * 900);
    out.push({
      ...t,
      id: id++,
      time: `${pad(stamp.getHours())}:${pad(stamp.getMinutes())}:${pad(stamp.getSeconds())}`,
    });
  }
  return out;
}
